import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  email: z.string().email("Email inválido"),
  phone: z.string().min(10, "Teléfono inválido"),
  hotel_name: z.string().optional(),
  message: z.string().optional(),
});

export default function DemoForm({ sourcePage = "home" }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      hotel_name: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/demo-request`, {
        ...data,
        source_page: sourcePage,
      });
      
      if (response.data.success) {
        toast.success("¡Solicitud enviada!", {
          description: "Nos pondremos en contacto contigo pronto.",
        });
        form.reset();
      }
    } catch (error) {
      toast.error("Error al enviar", {
        description: "Por favor intenta de nuevo más tarde.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 md:p-8 shadow-sm" data-testid="demo-form">
      <h3 className="heading-3 mb-2">Contáctanos</h3>
      <p className="body-text mb-6">
        Descubre cómo Zimoo puede aumentar los ingresos de tu hotel.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre completo *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Tu nombre"
                    {...field}
                    data-testid="demo-form-name"
                    className="border-black/10 focus:border-[#1ABCB4]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="tu@email.com"
                      {...field}
                      data-testid="demo-form-email"
                      className="border-black/10 focus:border-[#1ABCB4]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Teléfono *</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+52 998 123 4567"
                      {...field}
                      data-testid="demo-form-phone"
                      className="border-black/10 focus:border-[#1ABCB4]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="hotel_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre del Hotel</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Hotel XYZ"
                    {...field}
                    data-testid="demo-form-hotel"
                    className="border-black/10 focus:border-[#1ABCB4]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mensaje (opcional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="¿En qué podemos ayudarte?"
                    {...field}
                    data-testid="demo-form-message"
                    className="border-black/10 focus:border-[#1ABCB4] min-h-[100px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full"
            data-testid="demo-form-submit"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              "Enviar Mensaje"
            )}
          </Button>

          <p className="text-xs text-center text-[#404040]">
            Al enviar este formulario aceptas ser contactado por nuestro equipo.
          </p>
        </form>
      </Form>
    </div>
  );
}
